<template>
    <GeneralModal v-model:open="open">
        <div>
            <div
                class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100"
            >
                <PhoneIcon class="h-6 w-6 text-green-600" aria-hidden="true" />
            </div>
            <div class="mt-3 text-center sm:mt-5">
                <DialogTitle
                    as="h3"
                    class="text-base font-semibold leading-6 text-gray-900"
                    >Let's start with your phone number</DialogTitle
                >
                <div class="mt-2">
                    <p class="text-sm text-gray-500"></p>
                </div>
                <div class="mt-2">
                    <label
                        for="phone-number"
                        class="block text-sm font-medium leading-6 text-gray-900"
                        >Phone Number</label
                    >
                    <div class="relative mt-2 rounded-md shadow-sm">
                        <div
                            class="absolute inset-y-0 left-0 flex items-center"
                        >
                            <label for="country" class="sr-only">Country</label>
                            <select
                                id="country"
                                v-model="customer.phoneCode"
                                name="country"
                                autocomplete="country"
                                class="h-full rounded-md border-0 bg-transparent py-0 pl-3 pr-7 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
                            >
                                <option
                                    v-for="country in countries"
                                    :key="country.code"
                                    :value="country.phoneCode"
                                >
                                    {{ country.code }}
                                </option>
                            </select>
                        </div>
                        <input
                            ref="phoneInput"
                            type="text"
                            v-model="customer.phoneNumber"
                            @input="formatPhoneNumber($event.target?.value)"
                            @keyup.enter="checkCustomer"
                            name="phone-number"
                            autofocus
                            id="phone-number"
                            class="block w-full rounded-md border-0 py-1.5 pl-16 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-md sm:leading-6"
                            :placeholder="placeholder"
                        />
                    </div>
                </div>
            </div>
        </div>
        <div class="mt-5 sm:mt-6 flex justify-center">
            <GeneralButton
                @click="checkCustomer"
                :loading="loading"
                :disabled="loading"
                class="text-md"
                >Start Chatting</GeneralButton
            >
        </div>
    </GeneralModal>
</template>

<script setup lang="ts">
import { DialogTitle } from "@headlessui/vue";
import { PhoneIcon } from "@heroicons/vue/24/outline";
import GeneralModal from "./general/GeneralModal.vue";
import GeneralButton from "./general/GeneralButton.vue";
import {
    parsePhoneNumberFromString,
    getCountries,
    getCountryCallingCode,
    getExampleNumber,
} from "libphonenumber-js/max";
import examples from "libphonenumber-js/mobile/examples";
import { watch, ref, onMounted } from "vue";
import { useNavigatorLanguage, useFocus } from "@vueuse/core";
import { useFirebase } from "../composables/firebase";
const { isAuthenticated, user, customer } = useFirebase();
const open = defineModel("open", { type: Boolean, default: false });
const emit = defineEmits(["check-customer"]);
const loading = defineModel("loading", { type: Boolean, default: false });
const { language } = useNavigatorLanguage();

const phoneInput = ref<HTMLInputElement | null>(null);
const { focused } = useFocus(phoneInput, {
    initialValue: true,
    preventScroll: true, // prevents the page from scrolling when focusing
});
const checkCustomer = () => {
    emit("check-customer");
};
const formatPhoneNumber = (value: any) => {
    // Remove all non-digit characters
    let phoneNumber = value.replace(/\D/g, "");

    try {
        // Create the full international number
        const fullNumber = `+${customer.value.phoneCode || "1"}${phoneNumber}`;

        // Parse the phone number with the selected country code
        const parsedNumber = parsePhoneNumberFromString(fullNumber);

        if (parsedNumber) {
            // Store the formatted E.164 format without the '+' (e.g., "16504415689")
            customer.value.formattedPhoneNumber = parsedNumber
                .format("E.164")
                .slice(1);
            // Display the national format in the input (e.g., "(650) 441-5689")
            customer.value.phoneNumber = parsedNumber.formatNational();
        } else {
            // If parsing fails, fallback to raw input without '+'
            customer.value.formattedPhoneNumber = `${
                customer.value.phoneCode || "1"
            }${phoneNumber}`;
            customer.value.phoneNumber = phoneNumber;
        }
    } catch (error) {
        // If there's any error, fallback to raw input without '+'
        customer.value.formattedPhoneNumber = `${
            customer.value.phoneCode || "1"
        }${phoneNumber}`;
        customer.value.phoneNumber = phoneNumber;
    }
};

const priorityCountries = ["US", "GB", "CA", "ES", "FR", "DE", "IT"]; // Add your priority countries here

const getCountryName = (countryCode: string) => {
    try {
        // Try using Intl.DisplayNames if available
        if (typeof Intl !== "undefined" && Intl.DisplayNames) {
            return new Intl.DisplayNames(["en"], { type: "region" }).of(
                countryCode,
            );
        }

        // Fallback: return the country code if Intl.DisplayNames is not available
        return countryCode;
    } catch {
        // Fallback in case of any errors
        return countryCode;
    }
};

const countries = getCountries()
    .map((countryCode: string) => ({
        code: countryCode,
        name: getCountryName(countryCode),
        phoneCode: getCountryCallingCode(countryCode as any),
    }))
    .sort((a: any, b: any) => {
        // Put priority countries first
        const aPriority = priorityCountries.indexOf(a.code);
        const bPriority = priorityCountries.indexOf(b.code);
        if (aPriority !== -1 || bPriority !== -1) {
            return (
                (aPriority === -1 ? Infinity : aPriority) -
                (bPriority === -1 ? Infinity : bPriority)
            );
        }
        // Sort rest alphabetically
        return a.name.localeCompare(b.name);
    });

const getPlaceholder = (phoneCode: string) => {
    const countryCode: any =
        countries.find((c) => c.phoneCode === phoneCode)?.code || "US";
    try {
        const example = getExampleNumber(countryCode, examples);
        return example ? example.formatNational() : "(555) 987-6543";
    } catch {
        return "(555) 987-6543";
    }
};

// Watch for phone code changes to update placeholder
watch(
    () => customer.value.phoneCode,
    (newCode) => {
        placeholder.value = getPlaceholder(newCode);
    },
);

const placeholder = ref(getPlaceholder(customer.value?.phoneCode || "1"));
// Get initial country code based on browser language
const getInitialCountryCode = () => {
    try {
        if (!language?.value || typeof language.value !== "string") {
            return "1"; // default to US
        }

        // language.value could be 'en-US', 'es-ES', or just 'US'
        const parts = language.value.split("-");
        const countryCode = parts.length > 1 ? parts[1] : parts[0];

        if (!countryCode || countryCode.length !== 2) {
            return "1"; // default to US if no country code or invalid length
        }

        // Find the phone code for the detected country
        const country = countries.find(
            (c) => c.code === countryCode.toUpperCase(),
        );
        return country?.phoneCode || "1"; // default to US if country not found
    } catch (error) {
        console.log("Error detecting country code:", error);
        return "1"; // default to US if any error occurs
    }
};

// Set initial phone code when component mounts
onMounted(() => {
    console.log("nav language", language?.value);
    customer.value.phoneCode = getInitialCountryCode();
    console.log("initial country code", customer.value?.phoneCode);
});

// Watch for modal open state to refocus if needed
watch(
    () => open.value,
    (isOpen) => {
        if (isOpen) {
            focused.value = true;
        }
    },
);
</script>
